import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChartConstants } from '@constants/pages/chart-constants';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import { Signal } from '@models/dashboard/signal';
import { ChartSignalEntry } from '@models/pages/user-defined-page/chart-signal-entry';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { TranslateService } from '@ngx-translate/core';
import { SortingService } from '@services/administration/sorting/sorting.service';
import { ChartsConfigurationHelperService } from '@services/charts/helper/charts-configuration-helper.service';
import { NotificationService } from '@services/notification/notification.service';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';

@Component({
    selector: 'scitis-bar-widget-configuration',
    templateUrl: './bar-widget-configuration.component.html',
    styleUrls: ['./bar-widget-configuration.component.scss'],
    standalone: false
})
export class BarWidgetConfigurationComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();

  @Input()
  machineSignals: Signal[] = [];

  @Input()
  isTemplateWidget: boolean = false;

  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  hasDeletedSignalsChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  selectedSignalIds: number[] = [];
  usableSignals: Signal[] = [];

  selectedSignalForEdit: Map<number, boolean> = new Map<number, boolean>();
  dragAndDropItems = 'BAR_CHART_SIGNALS';
  subscription = new Subscription();
  hasDeletedSignalsMap: Map<string, boolean> = new Map<string, boolean>();

  constructor(private translate: TranslateService,
              private notification: NotificationService,
              private chartsConfigurationHelperService: ChartsConfigurationHelperService,
              private sortingService: SortingService,
              private dragulaService: DragulaService) {
    this.initializeDragAndDropSubscriptionEvents();
    this.chartsConfigurationHelperService.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignalsMap = hasDeletedSignals;
    });
  }

  public get isBarWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.BAR;
  }

  public get isAxisNameNotSet(): boolean {
    return !this.userDefinedWidget.userDefinedChartDetails.axisYLabel || this.userDefinedWidget.userDefinedChartDetails.axisYLabel.isNullOrWhitespace();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes?.userDefinedChartDetails && this.usableSignals.length > 0) {
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
      this.validateConfigurationAndEmitEvent();
    }
    if(this.usableSignals && (this.userDefinedWidget.userDefinedChartDetails.signals?.length ?? 0) > 0) {
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
      this.validateConfigurationAndEmitEvent();
    }
  }


  ngOnInit(): void {
    this.userDefinedWidget.userDefinedChartDetails.signals ??= [];
    this.usableSignals = [...this.chartsConfigurationHelperService.filterMisconfiguredOrUnusableSignals(this.machineSignals)];
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
    this.validateConfigurationAndEmitEvent();
  }

  @HostListener('document:keydown.escape') onKeydownHandler() {
    this.dragulaService.destroy(this.dragAndDropItems);
  }

  onAxisLabelChange() {
    this.validateConfigurationAndEmitEvent();
  }

  onAxisRangeChanged($event: number[]) {
    this.userDefinedWidget.userDefinedChartDetails.minY = $event[0];
    this.userDefinedWidget.userDefinedChartDetails.maxY = $event[1];
    this.validateConfigurationAndEmitEvent();
  }

  onSignalSelected($event: Signal | Signal[]) {
    this.userDefinedWidget.userDefinedChartDetails.signals = [];
    if (!$event) {
      this.validateConfigurationAndEmitEvent();
      return;
    }

    const signals: Signal[] = Array.isArray($event) ? $event : [$event];
    for (const signal of signals) {
      if (this.shouldShowTooManySignalWarning()) {
        continue;
      }

      const chartSignal: ChartSignalEntry = new ChartSignalEntry(signal);
      this.selectedSignalForEdit.set(signal.id, false);
      this.userDefinedWidget.userDefinedChartDetails.signals = [...this.userDefinedWidget.userDefinedChartDetails.signals, chartSignal];
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
    }
    this.userDefinedWidget.userDefinedChartDetails.signals = this.sortByLabel(this.userDefinedWidget.userDefinedChartDetails.signals);
    this.validateConfigurationAndEmitEvent();

  }

  sortByLabel(signals: ChartSignalEntry[]): ChartSignalEntry[] {
    return this.sortingService.sortArrayByProperty(signals, 'label', true);
  }

  hasSignalBeenDeleted(signal: ChartSignalEntry): boolean {
    return !this.usableSignals.find(s => s.id == signal.signalId);
  }

  public onSignalStartEdit($event: number) {
    this.setSelectedSignalForEdit($event);
  }

  public onSignalRemove($event: number) {
    this.userDefinedWidget.userDefinedChartDetails.signals.splice(this.userDefinedWidget.userDefinedChartDetails.signals.findIndex(a => a.signalId === $event), 1);
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
    this.validateConfigurationAndEmitEvent();
  }

  private hasDeletedSignals() {
    return (this.userDefinedWidget.userDefinedChartDetails.signals ?? []).some(signal =>
      signal.signalId > 0 && !this.usableSignals.find(s => s.id == signal.signalId)
    );
  }

  private shouldShowTooManySignalWarning(): boolean {
    if (this.userDefinedWidget.userDefinedChartDetails.signals.length < ChartConstants.MAX_NUMBER_OF_SIGNAL_ON_BAR_WIDGET) {
      return false;
    }

    this.notification.error(this.translate.instant('There are too many selected signals'), null, 'BarWidgetConfigurationComponent', true);
    return true;
  }

  private setSelectedSignalForEdit(id: number) {
    for (const signal of this.userDefinedWidget.userDefinedChartDetails.signals) {
      if (signal.signalId && signal.signalId === id) {
        this.selectedSignalForEdit.set(signal.signalId, true);
      } else {
        this.selectedSignalForEdit.set(signal.signalId, false);
      }
    }
  }

  private validateConfigurationAndEmitEvent() {
    let selectedSignalIds: number [] = [];
    this.setSelectedSignals();
    this.hasDeletedSignalsChanged.emit(this.hasDeletedSignals());
    const {userDefinedChartDetails} = this.userDefinedWidget;
    if (!userDefinedChartDetails || !userDefinedChartDetails.signals || userDefinedChartDetails.signals.length === 0) {

      this.selectedSignalIds = [...selectedSignalIds];
      const validConfiguration: boolean = this.verifyConfiguration();
      this.isConfigurationValid.emit(validConfiguration);
      return;
    }

    if (this.isTemplateWidget) {
      const selectedSignals = userDefinedChartDetails.signals.map(signal => {
        return {signalName: signal.signalName};
      });
      selectedSignalIds = this.chartsConfigurationHelperService.getSelectedSignalIds(selectedSignals, this.usableSignals);
    } else {
      const chartSignalEntries: ChartSignalEntry[] = userDefinedChartDetails.signals;
      selectedSignalIds = this.chartsConfigurationHelperService.getSelectedSignalIdsById(chartSignalEntries, this.usableSignals);
    }

    this.selectedSignalIds = [...selectedSignalIds];
    const validConfiguration: boolean = this.verifyConfiguration();
    this.isConfigurationValid.emit(validConfiguration);
  }

  private verifyConfiguration(): boolean {
    if (!this.userDefinedWidget.userDefinedChartDetails.axisYLabel || this.userDefinedWidget.userDefinedChartDetails.axisYLabel.isNullOrWhitespace()) {
      return false;
    }

    if (!this.verifyRange(this.userDefinedWidget.userDefinedChartDetails.minY, this.userDefinedWidget.userDefinedChartDetails.maxY)) {
      return false;
    }

    if (!this.userDefinedWidget.userDefinedChartDetails.signals || this.userDefinedWidget.userDefinedChartDetails.signals.length === 0) {
      return false;
    }

    return true;

  }

  private verifyRange(minY: number | undefined, maxY: number | undefined): boolean {
    if (!minY) {
      minY = 0;
    }

    if (!maxY) {
      maxY = 0;
    }

    if (minY === 0 && maxY === 0) {
      return true;
    }

    return minY < maxY;
  }

  private initializeDragAndDropSubscriptionEvents() {
    this.subscription.add(this.dragulaService.drag(this.dragAndDropItems)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
        })
    );
    this.subscription.add(this.dragulaService.drop(this.dragAndDropItems)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
        })
    );
    this.subscription.add(this.dragulaService.over(this.dragAndDropItems)
        .subscribe(({container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropItems)
        .subscribe(({container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private setSelectedSignals() {
    if (this.userDefinedWidget.userDefinedChartDetails && this.userDefinedWidget.userDefinedChartDetails.signals) {
      const selectedSignals = this.userDefinedWidget.userDefinedChartDetails.signals;
      const selectedSignalIds: number[] = this.chartsConfigurationHelperService.getSelectedSignalIdsById(selectedSignals, this.usableSignals);
      this.selectedSignalIds = [...selectedSignalIds];
    }
  }

}
